'use client';

import React, { useMemo, useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import axios from 'axios';
import useFetchRowPricing from '../../../hooks/useFetchRowPricing';
import { toast } from 'react-toastify';

const itemCategoryMap = {
  'Biological Survey': 'Survey',
  'Class Location/HCA Study': 'Environmental',
  'Temporary Sites (bores, construction)': 'ROW',
  'Preliminary Survey': 'Survey',
  'Title Agent Fees': 'ROW',
  'Archeological Survey': 'Survey',
  'ROW Lease Fees': 'ROW',
  'Above Ground Sites (meter, L/R)': 'ROW',
  'Road Crossing Permits': 'ROW',
  'ROW Agent Fees': 'ROW',
  'Access fees (roads)': 'ROW',
  'Plats, Alignment Sheets': 'Survey',
  'As-Built Survey': 'Survey',
  'SWPPP Services': 'Environmental',
};

// PATCH request for a single row
const useUpdateRowPricing = () => {
  const updatePrice = async (id, price) => {
    try {
      const patchDoc = [
        {
          op: 'replace',
          path: '/price',
          value: price,
        },
      ];

      const response = await axios.patch(`/api/PipelinePricing/row/update-pricing/${id}`, patchDoc, {
        headers: {
          'Content-Type': 'application/json-patch+json',
        },
      });

      return { success: true, message: response.data };
    } catch (error) {
      console.error('Update failed:', error);
      return { success: false, message: error?.response?.data || error.message };
    }
  };

  return { updatePrice };
};

const RowPricingGrid = () => {
  const { rowPricing, loading, error } = useFetchRowPricing();
  const { updatePrice } = useUpdateRowPricing();
  const [rowPricingData, setRowPricingData] = useState([]);
  const [editedRows, setEditedRows] = useState(new Map());

  useEffect(() => {
    if (!loading && !error) {
      const updatedWithCategories = rowPricing.map((row) => ({
        ...row,
        category: itemCategoryMap[row.item] || '',
      }));
      setRowPricingData(updatedWithCategories);
      setEditedRows(new Map());
    }
  }, [rowPricing, loading, error]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: 'Category',
        field: 'category',
        flex: 1,
        cellStyle: { backgroundColor: '#f9f9f9', color: '#333' },
      },
      {
        headerName: 'Item',
        field: 'item',
        flex: 2,
        cellStyle: { backgroundColor: '#f0f0f0', color: '#333' },
      },
      {
        headerName: 'Price',
        field: 'price',
        editable: true,
        flex: 1,
        valueFormatter: ({ value }) =>
          value === '' || value === null || isNaN(Number(value)) ? '' : Number(value).toLocaleString(),
      },
      {
        headerName: 'Unit',
        field: 'unit',
        flex: 1,
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
      },
    ],
    []
  );

  const handleGridEdit = (event) => {
    const updated = [...rowPricingData];
    updated[event.node.rowIndex] = event.data;
    setRowPricingData(updated);

    const newEdited = new Map(editedRows);
    newEdited.set(event.data.id, event.data.price);
    setEditedRows(newEdited);
  };

  const handleUpdatePricing = async () => {
    if (editedRows.size === 0) {
      toast.info('No changes to update.');
      return;
    }

    let allSucceeded = true;
    for (const [id, price] of editedRows.entries()) {
      const result = await updatePrice(id, price);
      if (!result.success) {
        allSucceeded = false;
        toast.error(`Failed to update row ${id}: ${result.message}`);
      }
    }

    if (allSucceeded) {
      toast.success('Pricing updated successfully!');
      setEditedRows(new Map());
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="h6"
        mb={2}
        sx={{
          backgroundColor: '#0C1D3C',
          color: 'white',
          padding: '8px 16px',
          borderRadius: 1,
        }}
      >
        ROW Pricing Table
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">
          {typeof error === 'string' ? error : error?.message || 'An unexpected error occurred.'}
        </Alert>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <Box className="ag-theme-alpine" style={{ height: '600px', width: '100%' }}>
              <AgGridReact
                rowData={rowPricingData}
                columnDefs={columnDefs}
                onCellValueChanged={handleGridEdit}
                defaultColDef={{ resizable: true }}
                domLayout="normal"
                getRowNodeId={(data) => data.id}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" color="primary" onClick={handleUpdatePricing}>
              Update Pricing
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default RowPricingGrid;
